import clsx from 'clsx';

import { IconicCheck2 } from '../../iconic-icons';
import { headingXxs, headingXxxs, textSm } from '../../typography';

import styles from './card-components.module.css';

type Props = {
  className?: string;
  children?: React.ReactNode;
};

const Card = ({ className, children }: Props) => <div className={clsx(styles.card, className)}>{children}</div>;

const CardWithInput = ({
  className,
  children,
  Input,
  Wrapper = 'div',
}: Props & {
  Input: React.ComponentType<{ hidden?: boolean }>;
  Wrapper?: React.ElementType<{ className?: string }>;
}) => (
  <Wrapper className={className}>
    <Input hidden />
    <Card className={styles.cardWithInput}>{children}</Card>
  </Wrapper>
);

const CardHeading = ({ className, children }: Props) => (
  <h4 className={clsx(className, styles.heading, headingXxxs)} data-testid='title'>
    {children}
  </h4>
);

const CardDescription = ({ className, children }: Props) => (
  <p className={clsx(className, styles.description, textSm)}>{children}</p>
);

const CardHighlightedPrice = ({ className, children }: Props) => (
  <span className={clsx(className, styles.highlightedPrice, headingXxs)}>{children}</span>
);

const CardChip: React.FC<Props> = ({ className, children }: Props) => (
  <span className={clsx(className, styles.chip, headingXxxs)}>
    <IconicCheck2 className={styles.check} />
    <span>{children}</span>
  </span>
);

type PropsWithIcon = Props & {
  icon?: React.ReactNode;
};

const CardContentWithIcon = ({ className, icon, children }: PropsWithIcon) => (
  <div className={clsx(className, styles.contentWithIconContainer)}>
    <div className={styles.iconContainer}>{icon}</div>
    <div className={styles.contentContainer}>{children}</div>
  </div>
);

const CardContentWithMiddledIcon = ({ className, icon, children }: PropsWithIcon) => (
  <div className={clsx(className, styles.contentWithMiddledIconContainer)}>
    <div className={styles.iconContainer}>{icon}</div>
    <div className={styles.contentContainer}>{children}</div>
  </div>
);

const VerticalCardContentWithIcon = ({ className, icon, children }: PropsWithIcon) => (
  <div className={clsx(className, styles.verticalContentWithIconContainer)}>
    <div className={styles.iconContainer}>{icon}</div>
    <div className={styles.contentContainer}>{children}</div>
  </div>
);

const CardContentWithoutIcon = ({ className, children }: Props) => (
  <div className={clsx(className, styles.contentWithoutIconContainer)}>
    <div className={styles.contentContainer}>{children}</div>
  </div>
);

export {
  Card,
  CardWithInput,
  CardHeading,
  CardDescription,
  CardHighlightedPrice,
  CardChip,
  CardContentWithIcon,
  CardContentWithMiddledIcon,
  VerticalCardContentWithIcon,
  CardContentWithoutIcon,
};
